/**
 * Clerk · Add Used Component
 * Search catalogue by brand + name + size, pick a match, enter qty + note,
 * bump the component's reusable pool.
 * Reuses:
 *   GET  /api/brands
 *   GET  /api/components
 *   POST /api/components/{id}/add-reusable   { quantity, note }
 */
import React, { useMemo, useState } from 'react';
import { Recycle, Check } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { EmptyState, SearchInput } from '../ui';

export default function AddUsedPage({ ctx }) {
  const { brands, components, refresh, showToast } = ctx;
  const [brandId, setBrandId] = useState('');
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const matches = useMemo(() => {
    const n = name.trim().toLowerCase();
    const s = size.trim().toLowerCase();
    return components
      .filter((c) => (brandId ? c.brand_id === brandId : true))
      .filter((c) => (n ? (c.name || '').toLowerCase().includes(n) : true))
      .filter((c) => (s ? (c.size || '').toLowerCase().includes(s) : true))
      .slice(0, 80);
  }, [components, brandId, name, size]);

  const submit = async () => {
    if (!selected || quantity < 1 || submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post(endpoints.components.addReusable(selected.id), {
        quantity: Number(quantity),
        note: note.trim() || undefined,
      });
      showToast(`Added ${quantity} unit(s) to re-issuable pool · ${selected.name}`, 'ok');
      setSelected(null);
      setQuantity(1);
      setNote('');
      setName(''); setSize(''); setBrandId('');
      await refresh.catalogue();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Failed to add to re-issuable pool.', 'bad');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cp-used-page">
      <div className="cp-eyebrow">Reusable Pool</div>
      <h1 className="cp-page-title">Add Used Component</h1>
      <p className="cp-page-lede">
        When you receive a cleared and sterilised component from the department, find it in
        the catalogue and add units to its reusable pool. The system will draw from this
        pool first on the next matching request.
      </p>

      <section className="cp-card cp-mt-5" data-testid="cp-used-search-card">
        <header className="cp-card-header">
          <div>
            <h3 className="cp-card-title">Locate the component</h3>
            <p className="cp-card-subtitle">Filter by brand, name and size — pick a match to continue.</p>
          </div>
        </header>

        <div className="cp-grid-2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div>
            <label className="cp-field-label" htmlFor="cp-used-brand">Brand</label>
            <select
              id="cp-used-brand"
              className="cp-select"
              value={brandId}
              onChange={(e) => { setBrandId(e.target.value); setSelected(null); }}
              data-testid="cp-used-brand"
            >
              <option value="">All brands</option>
              {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </div>
          <div>
            <label className="cp-field-label" htmlFor="cp-used-name">Component</label>
            <SearchInput
              value={name}
              onChange={(v) => { setName(v); setSelected(null); }}
              placeholder="e.g. Healing Abutment"
              testId="cp-used-name"
            />
          </div>
          <div>
            <label className="cp-field-label" htmlFor="cp-used-size">Size / Specification</label>
            <input
              id="cp-used-size"
              className="cp-input"
              type="text"
              value={size}
              onChange={(e) => { setSize(e.target.value); setSelected(null); }}
              placeholder="e.g. 4.3 × 11.5"
              data-testid="cp-used-size"
            />
          </div>
        </div>

        <div className="cp-mt-5">
          {matches.length === 0 ? (
            <EmptyState
              icon={Recycle}
              title="No components match"
              message="Loosen a filter, or check with SuperAdmin whether the catalogue has been seeded."
              testId="cp-used-no-results"
            />
          ) : (
            <div className="cp-result-list" data-testid="cp-used-results">
              {matches.map((c) => {
                const brand = c.brand_name
                  || (brands.find((b) => b.id === c.brand_id) || {}).name
                  || 'Unknown brand';
                const active = selected?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`cp-result-item${active ? ' is-selected' : ''}`}
                    onClick={() => setSelected(c)}
                    data-testid={`cp-used-pick-${c.id}`}
                  >
                    <strong>
                      {c.name}
                      {c.size ? ` · ${c.size}` : ''}
                    </strong>
                    <small>
                      {brand}
                      {'  ·  '}
                      Re-issuable <span className="cp-mono-num" style={{ color: 'var(--diis-accent-ink)', fontWeight: 600 }}>{c.reissuable_stock ?? c.reusable_stock ?? 0}</span>
                      {'  ·  '}
                      Fresh <span className="cp-mono-num">{c.stock_quantity ?? 0}</span>
                    </small>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {selected && (
        <section className="cp-card" data-testid="cp-used-confirm-card">
          <header className="cp-card-header">
            <div>
              <h3 className="cp-card-title">
                {selected.name}{selected.size ? ` · ${selected.size}` : ''}
              </h3>
              <p className="cp-card-subtitle">
                Current re-issuable pool · <strong>{selected.reissuable_stock ?? selected.reusable_stock ?? 0}</strong>
                {'  ·  '}
                Fresh stock · <strong>{selected.stock_quantity ?? 0}</strong>
              </p>
            </div>
          </header>

          <div className="cp-grid-2">
            <div>
              <label className="cp-field-label" htmlFor="cp-used-qty">Quantity received</label>
              <input
                id="cp-used-qty"
                type="number"
                min="1"
                className="cp-input"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value || '1', 10)))}
                data-testid="cp-used-quantity"
              />
            </div>
            <div>
              <label className="cp-field-label" htmlFor="cp-used-note">Note (optional)</label>
              <input
                id="cp-used-note"
                type="text"
                className="cp-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. autoclaved batch #12"
                data-testid="cp-used-note"
              />
            </div>
          </div>

          <div className="cp-mt-5" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setSelected(null)}
              disabled={submitting}
              data-testid="cp-used-cancel"
            >Cancel</button>
            <button
              type="button"
              className="cp-btn cp-btn-accent"
              onClick={submit}
              disabled={submitting || quantity < 1}
              data-testid="cp-used-submit"
            >
              <Check size={15} />
              {submitting ? 'Adding…' : `Add ${quantity} unit(s) to re-issuable pool`}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
